import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, map } from 'rxjs';
import * as yaml from 'js-yaml';
import { SharedModule } from 'src/app/shared/shared.module';
import { MatButtonModule } from '@angular/material/button';
import { MessageLevel, PgMessagesService } from 'src/app/shared/services/pg-messages.service';

interface ReleaseInfo {
  version: string;
  releaseDate: Date;
  downloadUrl: string;
}

interface YamlFileEntry {
  url: string;
  sha512: string;
  size: number;
  path: string;
}

interface YamlRelease {
  version: string;
  files: YamlFileEntry[];
  releaseDate: string;
}

interface ReleaseSource {
  baseUrl: string;
  yamlUrl: string;
  label: string;
}

@Component({
  selector: 'app-hello-release',
  templateUrl: './hello-release.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SharedModule,
    MatButtonModule
  ]
})
export class HelloReleaseComponent implements OnInit {
  releases: (ReleaseInfo & { label: string })[] = [];
  loading = true;

  private readonly releaseSources: ReleaseSource[] = [
    {
      baseUrl: 'https://uat-os.hcrise.com/hello-captain-release-win/',
      yamlUrl: 'https://uat-os.hcrise.com/hello-captain-release-win/latest.yml',
      label: 'Production'
    },
    {
      baseUrl: 'https://uat-os.hcrise.com/hello-captain-release-win-uat/',
      yamlUrl: 'https://uat-os.hcrise.com/hello-captain-release-win-uat/beta.yml',
      label: 'Beta / UAT'
    }
  ];

  constructor(
    private _http: HttpClient,
    private _pgMessagesService: PgMessagesService
  ) {}

  ngOnInit(): void {
    this.loadReleases();
  }

  loadReleases(): void {
    this.loading = true;
    const requests = this.releaseSources.map(source =>
      this._http.get(source.yamlUrl, { responseType: 'text' }).pipe(
        map((response: string) => this.parseYaml(response as string, source))
      )
    );

    forkJoin(requests).subscribe({
      next: (results) => {
        this.releases = results.filter(r => r !== null);
        this.loading = false;
      },
      error: () => {
        this._pgMessagesService.publishMessage({
          message: 'Could not fetch release information.',
          level: MessageLevel.Error,
          topic: 'Hello Captain'
        });
        this.loading = false;
      }
    });
  }

  private parseYaml(response: string, source: ReleaseSource): (ReleaseInfo & { label: string }) | null {
    try {
      const parsed: YamlRelease = yaml.load(response) as YamlRelease;
      return {
        version: parsed.version,
        releaseDate: new Date(parsed.releaseDate),
        downloadUrl: source.baseUrl + parsed.files[0].url,
        label: source.label
      };
    } catch {
      this._pgMessagesService.publishMessage({
        message: 'Failed to parse release: ' + source.label,
        level: MessageLevel.Error,
        topic: 'Hello Captain'
      });
      return null;
    }
  }

  copyLinkToClipboard(url: string): void {
    navigator.clipboard.writeText(url).then(() => {
      this._pgMessagesService.publishMessage({
        message: 'Download link copied to clipboard.',
        level: MessageLevel.Info,
        topic: 'Hello Captain'
      });
    }).catch(() => {
      this._pgMessagesService.publishMessage({
        message: 'Failed to copy link to clipboard.',
        level: MessageLevel.Error,
        topic: 'Hello Captain'
      });
    });
  }
}